// Client-side image downscaling for upload fields.
//
// Photos taken on a phone or tablet are routinely 3-8 MB, which is far more
// than any avatar or logo needs and big enough that nginx rejects the POST
// with a 413 before it ever reaches the API. Shrinking to a sane pixel size
// and re-encoding as JPEG turns those into ~50-150 KB, so the upload stays
// well inside every limit in the chain.

const DEFAULTS = { maxDim: 512, quality: 0.85, mimeType: 'image/jpeg' };

// Decode a File into something canvas can draw. createImageBitmap is the fast
// path and applies EXIF orientation for us (phone photos are frequently
// rotated); the <img> fallback covers browsers that don't have it.
async function decode(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
      // Some browsers reject the options bag rather than ignoring it.
      try { return await createImageBitmap(file); } catch (e2) { /* fall through */ }
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
}

// Returns a new File scaled so neither side exceeds maxDim. Falls back to the
// original file on any failure, or when shrinking wouldn't actually help —
// a bad resize must never block the user from uploading at all.
export async function downscaleImage(file, options = {}) {
  const { maxDim, quality, mimeType } = { ...DEFAULTS, ...options };
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // Already-small images and formats we'd damage by re-encoding are left alone.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  try {
    const source = await decode(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    const scale = Math.min(1, maxDim / Math.max(width, height));
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    // JPEG has no alpha; without this, transparent PNGs come out black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(source, 0, 0, targetW, targetH);
    if (typeof source.close === 'function') source.close();

    const blob = await canvasToBlob(canvas, mimeType, quality);
    if (!blob || blob.size >= file.size) return file;

    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    const ext = mimeType === 'image/png' ? '.png' : '.jpg';
    return new File([blob], `${base}${ext}`, { type: mimeType, lastModified: Date.now() });
  } catch (e) {
    return file;
  }
}

export default downscaleImage;
